import {
  getAddressColumn,
  getBankAccountColumn,
  getBirthDateColumn,
  getCivilStatusColumn,
  getEmailColumn,
  getFiscalResidenceColumn,
  getHoldingClassColumn,
  getInvestorTypeColumn,
  getLegalPersonColumn,
  getNameColumn,
  getNationalityColumn,
  getNroEmployeeColumn,
  getNroIdColumn,
  getNroPPSSColumn,
  getNroPSFinalColumn,
  getNroPSInitialColumn,
  getPrimaPSColumn,
  getTypeIdColumn,
} from 'constants/getExcelColumns';

import checkIsValidEmail from 'utils/checkIsValidEmail';
import normalizeCif from 'utils/normalizeCif';

import getIsNaturalPerson from 'modules/_shared/components/Modals/UploadPartner/utils/getIsNaturalPerson';

import {
  ConstitutionExcelRawRow,
  ConstitutionExcelRow,
  ConstitutionExcelValidationError,
  ConstitutionExcelValidationResult,
} from '../types/ConstitutionExcel';
import {
  assignConstitutionExcelRanges,
  parseExplicitRangeFromRaw,
} from './assignConstitutionExcelRanges';
import { HoldingClassForConstitutionExcel } from './initConstitutionNumerations';

type Partner = {
  cif?: string;
};

type ValidateConstitutionExcelRowsParams = {
  rawRows: ConstitutionExcelRawRow[];
  holdingClasses: HoldingClassForConstitutionExcel[];
  existingPartners: Partner[];
  isSocietyConstituted: boolean;
};

export const parseShares = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.floor(parsed);
};

const parsePremium = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
};

const isRowEmpty = (row: ConstitutionExcelRawRow): boolean =>
  Object.values(row).every(
    (value) =>
      value === undefined || value === null || String(value).trim() === ''
  );

const mapRawRowToExcelRow = (
  rawRow: ConstitutionExcelRawRow,
  rowIndex: number,
  existingCifs: string[]
): ConstitutionExcelRow => {
  const cif = getNroIdColumn(rawRow);
  const ppssShares = parseShares(getNroPPSSColumn(rawRow));
  const rangeFields = parseExplicitRangeFromRaw(
    rawRow,
    getNroPSInitialColumn,
    getNroPSFinalColumn,
    parseShares
  );

  let shares = ppssShares;
  if (
    rangeFields.hasExplicitRange &&
    rangeFields.shareFrom &&
    rangeFields.shareTo
  ) {
    shares = rangeFields.shareTo - rangeFields.shareFrom + 1;
  }

  return {
    rowIndex,
    name: String(getNameColumn(rawRow) || '').trim(),
    cif,
    shares,
    shareFrom: rangeFields.shareFrom,
    shareTo: rangeFields.shareTo,
    hasExplicitRange: rangeFields.hasExplicitRange,
    holdingClassName: String(getHoldingClassColumn(rawRow) || '').trim(),
    sharePremium: parsePremium(getPrimaPSColumn(rawRow)),
    email: getEmailColumn(rawRow),
    idType: String(getTypeIdColumn(rawRow) || '').trim(),
    nationality: String(getNationalityColumn(rawRow) || '').trim(),
    investorType: getInvestorTypeColumn(rawRow),
    birthdate: String(getBirthDateColumn(rawRow) || '').trim(),
    civilStatus: String(getCivilStatusColumn(rawRow) || '').trim(),
    employeeNumber: String(getNroEmployeeColumn(rawRow) || '').trim(),
    bankAccountNumber: String(getBankAccountColumn(rawRow) || '').trim(),
    isNaturalPerson: getIsNaturalPerson(getLegalPersonColumn(rawRow)),
    address: getAddressColumn(rawRow),
    taxAddress: getFiscalResidenceColumn(rawRow),
    isExistingPartner: existingCifs.includes(cif),
  };
};

const hasDuplicatedFieldsConflict = (rows: ConstitutionExcelRow[]): boolean => {
  for (let i = 0; i < rows.length - 1; i += 1) {
    for (let j = i + 1; j < rows.length; j += 1) {
      if (rows[i].cif === rows[j].cif) {
        const namesDiffer =
          rows[i].name.toUpperCase() !== rows[j].name.toUpperCase();
        const bothHaveEmail = Boolean(rows[i].email) && Boolean(rows[j].email);
        const emailsDiffer = bothHaveEmail && rows[i].email !== rows[j].email;

        if (namesDiffer || emailsDiffer) {
          return true;
        }
      }
    }
  }
  return false;
};

const findNewPartnerCategories = (
  rows: ConstitutionExcelRow[],
  existingCategories: string[]
): string[] => {
  const existing = existingCategories.map((category) => category.toUpperCase());
  const categoriesInExcel = rows
    .map((row) => row.investorType)
    .filter((category): category is string => Boolean(category));

  const uniqueCategories = [...new Set(categoriesInExcel)];

  return uniqueCategories.filter(
    (category) => !existing.includes(category.toUpperCase())
  );
};

const resolveHoldingClass = (
  holdingClassName: string,
  holdingClasses: HoldingClassForConstitutionExcel[],
  defaultHoldingClassId: string
): HoldingClassForConstitutionExcel | undefined => {
  if (!holdingClassName) {
    return holdingClasses.find(
      (holdingClass) => holdingClass._id === defaultHoldingClassId
    );
  }

  return holdingClasses.find(
    (holdingClass) =>
      holdingClass.name?.toUpperCase() === holdingClassName.toUpperCase()
  );
};

const validateRowRanges = (
  row: ConstitutionExcelRow,
  ppssShares: number
): ConstitutionExcelValidationError[] => {
  const rowErrors: ConstitutionExcelValidationError[] = [];
  const rangeFields = {
    hasFrom: row.shareFrom !== undefined,
    hasTo: row.shareTo !== undefined,
    hasExplicitRange: row.hasExplicitRange,
  };

  if (
    (rangeFields.hasFrom && !rangeFields.hasTo) ||
    (!rangeFields.hasFrom && rangeFields.hasTo)
  ) {
    rowErrors.push({
      code: 'INCOMPLETE_RANGE',
      rowIndex: row.rowIndex,
      cif: row.cif,
    });
    return rowErrors;
  }

  if (row.hasExplicitRange) {
    const shareFrom = row.shareFrom || 0;
    const shareTo = row.shareTo || 0;

    if (shareFrom < 1 || shareTo < shareFrom) {
      rowErrors.push({
        code: 'INVALID_RANGE',
        rowIndex: row.rowIndex,
        cif: row.cif,
      });
    }

    if (ppssShares > 0 && ppssShares !== row.shares) {
      rowErrors.push({
        code: 'RANGE_SHARES_MISMATCH',
        rowIndex: row.rowIndex,
        cif: row.cif,
      });
    }
  } else if (!row.shares) {
    rowErrors.push({ code: 'MISSING_SHARES', rowIndex: row.rowIndex });
  } else if (row.shares <= 0 || !Number.isInteger(row.shares)) {
    rowErrors.push({
      code: 'INVALID_SHARES',
      rowIndex: row.rowIndex,
      cif: row.cif,
    });
  }

  return rowErrors;
};

export const validateConstitutionExcelRows = ({
  rawRows,
  holdingClasses,
  existingPartners,
  isSocietyConstituted,
}: ValidateConstitutionExcelRowsParams): ConstitutionExcelValidationResult => {
  const errors: ConstitutionExcelValidationError[] = [];
  const defaultHoldingClassId = holdingClasses?.[0]?._id || '';

  if (isSocietyConstituted) {
    errors.push({ code: 'SOCIETY_ALREADY_CONSTITUTED' });
    return {
      isValid: false,
      errors,
      rows: [],
      totalShares: 0,
      newPartnerCategories: [],
    };
  }

  const filteredRawRows = rawRows.filter((row) => !isRowEmpty(row));

  if (!filteredRawRows.length) {
    errors.push({ code: 'NO_ROWS' });
    return {
      isValid: false,
      errors,
      rows: [],
      totalShares: 0,
      newPartnerCategories: [],
    };
  }

  const existingCifs = existingPartners
    .map((partner) => normalizeCif(partner?.cif || ''))
    .filter((cif) => cif);

  const rows = filteredRawRows.map((rawRow, index) =>
    mapRawRowToExcelRow(rawRow, index + 2, existingCifs)
  );

  const rowIndexesWithErrors = new Set<number>();

  rows.forEach((row, index) => {
    const rawRow = filteredRawRows[index];
    const ppssShares = parseShares(getNroPPSSColumn(rawRow));

    if (!row.name) {
      errors.push({ code: 'MISSING_NAME', rowIndex: row.rowIndex });
      rowIndexesWithErrors.add(row.rowIndex);
    }
    if (!row.cif) {
      errors.push({ code: 'MISSING_CIF', rowIndex: row.rowIndex });
      rowIndexesWithErrors.add(row.rowIndex);
    }

    const rangeErrors = validateRowRanges(row, ppssShares);
    rangeErrors.forEach((error) => {
      errors.push(error);
      rowIndexesWithErrors.add(row.rowIndex);
    });

    if (row.email) {
      const isValidEmail = checkIsValidEmail(row.email);
      if (!isValidEmail) {
        errors.push({
          code: 'INVALID_EMAIL',
          rowIndex: row.rowIndex,
          cif: row.cif,
          detail: row.email,
        });
        rowIndexesWithErrors.add(row.rowIndex);
      }
    }

    const holdingClass = resolveHoldingClass(
      row.holdingClassName,
      holdingClasses,
      defaultHoldingClassId
    );

    if (!holdingClass) {
      errors.push({
        code: 'UNKNOWN_HOLDING_CLASS',
        rowIndex: row.rowIndex,
        cif: row.cif,
        detail: row.holdingClassName,
      });
      rowIndexesWithErrors.add(row.rowIndex);
    }
  });

  if (hasDuplicatedFieldsConflict(rows)) {
    errors.push({ code: 'DUPLICATED_CIF_CONFLICT' });
  }

  const { overlapErrors } = assignConstitutionExcelRanges({
    rows,
    holdingClasses,
    rowIndexesWithErrors,
  });

  overlapErrors.forEach((error) => {
    errors.push(error);
    if (error.rowIndex) {
      rowIndexesWithErrors.add(error.rowIndex);
    }
  });

  const validRows = rows.filter(
    (row) => !rowIndexesWithErrors.has(row.rowIndex)
  );
  const totalShares = validRows.reduce((acc, row) => acc + row.shares, 0);

  return {
    isValid: errors.length === 0,
    errors,
    rows,
    totalShares,
    newPartnerCategories: findNewPartnerCategories(rows, []),
  };
};

export const validateConstitutionExcelRowsWithCategories = (
  params: ValidateConstitutionExcelRowsParams & {
    existingPartnerCategories: { name: string }[];
  }
): ConstitutionExcelValidationResult => {
  const result = validateConstitutionExcelRows(params);

  return {
    ...result,
    newPartnerCategories: findNewPartnerCategories(
      result.rows,
      params.existingPartnerCategories.map((category) => category.name)
    ),
  };
};

export const getHoldingClassIdForRow = (
  row: ConstitutionExcelRow,
  holdingClasses: HoldingClassForConstitutionExcel[]
): string => {
  const defaultHoldingClassId = holdingClasses?.[0]?._id || '';
  const holdingClass = resolveHoldingClass(
    row.holdingClassName,
    holdingClasses,
    defaultHoldingClassId
  );

  return holdingClass?._id || defaultHoldingClassId;
};
